// src/components/EventsTab.jsx
//
// EVENTS tab for PlayerView — shows registered events (with selector)
// and available events (with join). Only registered+active events can be selected.

import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import db from '../lib/db'
import cache from '../lib/localCache'
import useOnlineStatus from '../lib/useOnlineStatus'
import { Radio, CheckCircle, MapPin, Calendar, ChevronDown, ChevronRight, FileText } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const S = {
  display: { fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif", letterSpacing: '0.14em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  panel: { background: 'rgba(28, 38, 38, .6)', border: '1px solid rgba(140, 180, 130, .2)', backdropFilter: 'blur(6px)' },
  accent: { color: '#9adb6e' },
  muted: { color: '#8aa090' },
  faded: { color: '#5a6a60' },
  cyan: { color: '#4dd6c2' },
  warn: { color: '#ff6a3d' },
  sectionHeader: {
    fontFamily: "'Rajdhani', sans-serif", fontSize: '10px', fontWeight: 700,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: '#5a6a60',
    marginBottom: '8px', paddingBottom: '4px',
    borderBottom: '1px solid rgba(140, 180, 130, .1)',
  },
}

// ANU-39 — shared formatter for the start/end strip on each event card.
// Returns null when the GM hasn't set a schedule yet so the JSX caller
// can skip rendering. Format mirrors the GameEvent header strip
// ("Apr 30, 14:00 → Apr 30, 22:00").
function formatScheduleLine(starts_at, ends_at) {
  const fmt = (iso) => {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  const a = fmt(starts_at)
  const b = fmt(ends_at)
  if (!a && !b) return null
  return `${a || '?'} → ${b || '?'}`
}

export default function EventsTab({ userId }) {
  const isOnline = useOnlineStatus()
  const navigate = useNavigate()
  const [allEvents, setAllEvents] = useState([])
  const [myEvents, setMyEvents] = useState([])
  const [selectedEventId, setSelectedEventId] = useState(() =>
    localStorage.getItem('dc_active_event') || null
  )
  const [loading, setLoading] = useState(true)
  // Track 5.3 — error state for the events fetch so an RLS denial /
  // network blip surfaces a retry button instead of an empty list.
  const [error, setError] = useState(null)
  const [joining, setJoining] = useState(null)
  const [joinMessage, setJoinMessage] = useState(null)
  const [availableCollapsed, setAvailableCollapsed] = useState(true)
  // UX cleanup — past operations default collapsed (same pattern as
  // archived sections in the Dashboard). User's choice persists.
  const [pastCollapsed, setPastCollapsed] = useState(() =>
    localStorage.getItem('dc_events_past_collapsed') !== 'false'
  )
  const togglePast = () => setPastCollapsed(prev => {
    const next = !prev
    localStorage.setItem('dc_events_past_collapsed', String(next))
    return next
  })

  useEffect(() => { fetchEvents() }, [])

  const fetchEvents = async () => {
    setLoading(true); setError(null)
    try {
      // Get ALL events (for the available section)
      const events = await db.getAllEvents()
      setAllEvents(events || [])

      // Get every event I'm registered for, regardless of lifecycle stage.
      // getActiveEventsForUser would drop registration / prep / locked
      // events on the floor, so a fresh open-enrollment join never moved
      // out of the available bucket on the player's screen.
      const registered = await db.getRegisteredEventsForUser(userId)
      setMyEvents(registered || [])

      // Auto-select if only one active registered event and nothing selected.
      // Treat both 'live' (new lifecycle) and 'active' (legacy) as runtime-live.
      const activeRegistered = (registered || []).filter(e => e.status === 'live' || e.status === 'active')
      if (activeRegistered.length === 1 && !selectedEventId) {
        selectEvent(activeRegistered[0].id)
      }
    } catch (err) {
      console.warn('[EventsTab] fetchEvents failed', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  const selectEvent = (eventId) => {
    setSelectedEventId(eventId)
    localStorage.setItem('dc_active_event', eventId)
    window.dispatchEvent(new CustomEvent('dc_event_selected', { detail: { eventId } }))
  }

  const joinOpenEvent = async (eventId) => {
    if (!isOnline) return
    setJoining(eventId)
    setJoinMessage(null)

    // Pre-check: already on the roster? Surface a friendly message rather
    // than letting the unique (event_id, user_id) constraint blow up with a
    // raw 23505. Also covers the race where two tabs both fire JOIN.
    // Bug 61 re-open — prod event_players has no `id` column. Use
    // user_id for the existence check + reg_status for the pre-check.
    const { data: existing } = await supabase
      .from('event_players')
      .select('user_id, reg_status')
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .limit(1)
    if (existing?.[0]) {
      setJoinMessage({ kind: 'info', text: 'You are already registered for this event.' })
      // Still refresh so the available list updates to reflect reality.
      await cache.clear('events').catch(() => {})
      await fetchEvents()
      setJoining(null)
      return
    }

    const { error } = await supabase.from('event_players').insert({
      event_id: eventId,
      user_id: userId,
      event_role: 'player',
      reg_status: 'approved',
    })

    if (error) {
      console.warn('[EventsTab] join failed:', error)
      setJoinMessage({ kind: 'error', text: `Join failed: ${error.message || 'unknown error'}` })
      setJoining(null)
      return
    }

    // Bust the events SWR cache so the available list re-resolves with the
    // new registration applied (otherwise the joined event sometimes
    // lingers in the available bucket on the optimistic re-render).
    await cache.clear('events').catch(() => {})
    setJoinMessage({ kind: 'ok', text: 'Joined event.' })
    await fetchEvents()
    setJoining(null)
  }

  // Categorize based on REGISTRATION, not just event status. The lifecycle
  // map: live/active = runtime; prep/registration/locked/pending/scheduled
  // = pre-game (upcoming); endex/closed = wrap-up.
  const myRegisteredIds = myEvents.map(e => e.id)
  const activeRegistered = myEvents.filter(e => e.status === 'live' || e.status === 'active')
  const upcomingRegistered = myEvents.filter(e => ['prep','registration','locked','pending','scheduled'].includes(e.status))
  // Cancelled events join closed/endex in the wrap-up bucket so a player
  // who registered then saw the event killed still has a record of it.
  const closedRegistered = myEvents.filter(e => e.status === 'closed' || e.status === 'endex' || e.status === 'cancelled')

  // Available = events I'm NOT registered for, that have open registration
  // and are accepting players (registration / pending / scheduled / live).
  const availableEvents = allEvents.filter(e =>
    !myRegisteredIds.includes(e.id) &&
    ['registration','pending','scheduled','live','active'].includes(e.status) &&
    e.registration_mode === 'open'
  )

  // Track 5.3 — error / retry surface for the events fetcher. The
  // existing internal loading + empty-state branches stay (they're
  // category-aware: empty active, empty available, etc) so we only
  // route the top-level loading + error through the shared component.
  if (loading) {
    return <p style={{ ...S.faded, textAlign: 'center', padding: '24px', fontSize: '12px' }}>Loading events...</p>
  }
  if (error) {
    const msg = error?.friendlyMessage || error?.message || 'Failed to load events.'
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <p style={{ color: '#ff6a3d', fontSize: '13px', marginBottom: '8px', fontFamily: "'JetBrains Mono', monospace" }}>{msg}</p>
        <button onClick={fetchEvents} style={{
          fontFamily: "'Rajdhani', sans-serif", fontSize: '11px',
          letterSpacing: '0.12em', textTransform: 'uppercase',
          padding: '8px 16px', minHeight: 44, minWidth: 44,
          background: 'rgba(28,38,38,.6)', color: '#9adb6e',
          border: '1px solid rgba(154,219,110,.4)', cursor: 'pointer',
        }}>Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-4">

      {joinMessage && (
        <div className="p-3 text-xs flex items-center justify-between gap-2" style={{
          ...S.panel,
          border: `1px solid ${joinMessage.kind === 'error' ? 'rgba(255,106,61,.5)' : joinMessage.kind === 'ok' ? 'rgba(154,219,110,.5)' : 'rgba(140,180,130,.3)'}`,
          color: joinMessage.kind === 'error' ? '#ff6a3d' : joinMessage.kind === 'ok' ? '#9adb6e' : '#8aa090',
          fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.08em',
        }}>
          <span>{joinMessage.text}</span>
          <button onClick={() => setJoinMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.7 }}>×</button>
        </div>
      )}

      {/* ── REGISTERED: Active Operations (with selector) ── */}
      {activeRegistered.length > 0 && (
        <div>
          <p style={S.sectionHeader}>
            <Radio size={10} style={{ display: 'inline', marginRight: '4px' }} />
            Active Operations
          </p>
          <div className="space-y-2">
            {activeRegistered.map(event => {
              const isSelected = selectedEventId === event.id
              return (
                <div key={event.id} className="p-3 relative overflow-hidden" style={{
                  ...S.panel,
                  border: isSelected ? '1px solid #9adb6e' : '1px solid rgba(140, 180, 130, .2)',
                  boxShadow: isSelected ? '0 0 8px rgba(154, 219, 110, .3)' : 'none',
                }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm truncate" style={{ ...S.display, color: isSelected ? '#9adb6e' : '#e0e8de' }}>
                        {event.name}
                      </p>
                      <p className="text-xs mt-0.5" style={S.faded}>
                        {event.organizations?.name || 'Independent'}
                      </p>
                      {formatScheduleLine(event.starts_at, event.ends_at) && (
                        <p className="text-xs mt-0.5 flex items-center gap-1" style={S.muted}>
                          <Calendar size={10} /> {formatScheduleLine(event.starts_at, event.ends_at)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isSelected ? (
                        <span className="flex items-center gap-1 text-xs px-2 py-1" style={{
                          background: 'rgba(154, 219, 110, .12)', color: '#9adb6e',
                          border: '1px solid rgba(154, 219, 110, .3)',
                          ...S.display, fontSize: '10px',
                        }}>
                          <CheckCircle size={11} /> ACTIVE OP
                        </span>
                      ) : (
                        <button onClick={() => selectEvent(event.id)} style={{
                          ...S.display, fontSize: '10px',
                          background: 'rgba(77, 214, 194, .08)', color: '#4dd6c2',
                          border: '1px solid rgba(77, 214, 194, .3)',
                          padding: '4px 10px', cursor: 'pointer',
                        }}>
                          SELECT
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── REGISTERED: Upcoming ── */}
      {upcomingRegistered.length > 0 && (
        <div>
          <p style={S.sectionHeader}>
            <Calendar size={10} style={{ display: 'inline', marginRight: '4px' }} />
            Registered — Upcoming
          </p>
          <div className="space-y-2">
            {upcomingRegistered.map(event => (
              <div key={event.id} className="p-3" style={S.panel}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ ...S.display, color: '#e0e8de' }}>{event.name}</p>
                    <p className="text-xs mt-0.5" style={S.faded}>{event.organizations?.name || 'Independent'}</p>
                    {formatScheduleLine(event.starts_at, event.ends_at) && (
                      <p className="text-xs mt-0.5 flex items-center gap-1" style={S.muted}>
                        <Calendar size={10} /> {formatScheduleLine(event.starts_at, event.ends_at)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                    <span className="text-xs px-2 py-0.5" style={{
                      ...S.display, fontSize: '9px',
                      background: 'rgba(77, 214, 194, .08)', color: '#4dd6c2',
                      border: '1px solid rgba(77, 214, 194, .2)',
                    }}>
                      REGISTERED
                    </span>
                    <span style={{ ...S.display, fontSize: '8px', color: '#5a6a60' }}>
                      {event.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── REGISTERED: Closed (with AAR/dossier link) ── */}
      {closedRegistered.length > 0 && (
        <div>
          <button onClick={togglePast} className="w-full flex items-center justify-between"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}>
            <span style={S.sectionHeader}>
              <FileText size={10} style={{ display: 'inline', marginRight: '4px' }} />
              Past Operations — {closedRegistered.length}
            </span>
            <span style={{ ...S.faded, fontSize: 10 }}>{pastCollapsed ? '▼ show' : '▲ hide'}</span>
          </button>
          {!pastCollapsed && (
          <div className="space-y-2">
            {closedRegistered.map(event => (
              <button key={event.id} onClick={() => navigate(`/event/${event.id}/dossiers`)}
                className="w-full p-3 text-left" style={{ ...S.panel, cursor: 'pointer' }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ ...S.display, color: '#e0e8de' }}>{event.name}</p>
                    <p className="text-xs mt-0.5" style={S.faded}>
                      {event.organizations?.name || 'Independent'}
                      {event.closed_at ? ` · closed ${new Date(event.closed_at).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <span style={{
                    ...S.display, fontSize: '10px',
                    background: 'rgba(192,138,42,.12)', color: '#c08a2a',
                    border: '1px solid rgba(192,138,42,.4)',
                    padding: '4px 10px', flexShrink: 0,
                  }}>
                    DOSSIER →
                  </span>
                </div>
              </button>
            ))}
          </div>
          )}
        </div>
      )}

      {/* ── Empty state for registered ── */}
      {activeRegistered.length === 0 && upcomingRegistered.length === 0 && closedRegistered.length === 0 && (
        <div className="text-center py-6" style={S.faded}>
          <Calendar size={28} className="mx-auto mb-2 opacity-50" />
          <p style={{ ...S.display, fontSize: '12px' }}>No registered events</p>
          <p className="text-xs mt-1">Ask your GM for a join link or check available events below</p>
        </div>
      )}

      {/* ── AVAILABLE: Open Registration (collapsible) ── */}
      {availableEvents.length > 0 && (
        <div>
          <button onClick={() => setAvailableCollapsed(!availableCollapsed)}
            className="w-full flex items-center justify-between" style={{ ...S.sectionHeader, cursor: 'pointer', border: 'none', background: 'none', padding: 0, paddingBottom: '4px' }}>
            <span>
              <MapPin size={10} style={{ display: 'inline', marginRight: '4px' }} />
              Available — Open Registration ({availableEvents.length})
            </span>
            <span style={{ fontSize: '10px' }}>
              {availableCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            </span>
          </button>
          {!availableCollapsed && (
            <div className="space-y-2">
              {availableEvents.map(event => (
                <div key={event.id} className="p-3" style={S.panel}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate" style={{ ...S.display, color: '#e0e8de' }}>{event.name}</p>
                      <p className="text-xs mt-0.5" style={S.faded}>{event.organizations?.name || 'Independent'}</p>
                      {formatScheduleLine(event.starts_at, event.ends_at) && (
                        <p className="text-xs mt-0.5 flex items-center gap-1" style={S.muted}>
                          <Calendar size={10} /> {formatScheduleLine(event.starts_at, event.ends_at)}
                        </p>
                      )}
                    </div>
                    <button onClick={() => joinOpenEvent(event.id)} disabled={joining === event.id || !isOnline}
                      style={{
                        ...S.display, fontSize: '10px',
                        background: joining === event.id ? '#5a6a60' : '#9adb6e',
                        color: '#14181a', border: 'none',
                        padding: '6px 12px', cursor: joining === event.id ? 'wait' : 'pointer',
                        flexShrink: 0, opacity: !isOnline ? 0.4 : 1,
                      }}>
                      {joining === event.id ? 'JOINING...' : 'JOIN'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
